"use client";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useJobFilterStore } from "../../store/useJobFilterStore";
import { useEducationStore } from "../../store/useEducationStore";
import { toast } from "react-toastify";
import { useCandidateProfileStore } from "../../store/useCandidateProfileStore";
import DatePicker from "react-datepicker";


export type Education = {
  univTitle: string;
  univDegree: string;
  univMajor: string;
  startDate: string;
  endDate: string;
  current: boolean;
};

type EducationField = keyof Education;

type Props = {
  onClose: () => void;
  setEducation: (data: Education[]) => void;
  defaultData: Education[];
};

export default function EditEducationForm({
  onClose,
  setEducation,
  defaultData,
}: Props) {
  const [educations, setEducations] = useState<Education[]>(defaultData);
  const { updateEducations, loading } = useEducationStore();

  const location = useLocation();
  const lang = location.pathname.startsWith("/id") ? "id" : "en";

  const { degrees, fetchFilters } = useJobFilterStore();

  useEffect(() => {
    if (lang) fetchFilters(lang, "degrees");
  }, [lang]);

  const toDate = (value?: string) =>
    value ? new Date(value) : null;

  const toString = (date: Date | null) =>
    date ? date.toISOString().slice(0, 10) : "";

  const handleChange = (i: number, name: EducationField, value: any) => {
    setEducations((prev) => {
      const updated = [...prev];
      updated[i] = { ...updated[i], [name]: value };
      if (name === "current" && value === true) {
        updated[i].endDate = "";
      }
      return updated;
    });
  };

  const addEducation = () => {
    setEducations((prev) => [
      ...prev,
      {
        univTitle: "",
        univDegree: "",
        univMajor: "",
        startDate: "",
        endDate: "",
        current: false,
      },
    ]);
  };

  const removeEducation = (index: number) => {
    setEducations((prev) => prev.filter((_, i) => i !== index));
  };

  const { fetchCandidateProfile } = useCandidateProfileStore();
  const handleSave = async () => {
    console.log("FORM DATA:", educations);

    const success = await updateEducations(
      educations.map((e) => ({
        ...e,
        univDegree: e.univDegree === "" ? "" : Number(e.univDegree),
      }))
    );

    if (success) {
      setEducation(educations);
      fetchCandidateProfile();
      toast.success("Educations updated successfully");
      onClose();
    } else {
      toast.error("Failed to update educations");
    }
  };

  return (
    <div className="space-y-10 pb-4">
      {educations.map((exp, index) => (
        <div key={index} className="border rounded-xl p-6 space-y-6 relative">
          {educations.length > 1 && (
            <button
              onClick={() => removeEducation(index)}
              className="absolute top-4 right-4 text-red-500 text-sm font-semibold"
            >
              ✕ {lang === "id" ? "Hapus" : "Remove"}
            </button>
          )}

          <h3 className="text-lg font-semibold">{lang === "id" ? "Pendidikan" : "Education"} {index + 1}</h3>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">{lang === "id" ? "Sekolah/Universitas" : "School/University"}</label>
              <input
                className="w-full border rounded-lg p-3"
                value={exp.univTitle}
                onChange={(e) => handleChange(index, "univTitle", e.target.value)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{lang === "id" ? "Gelar" : "Degree"}</label>
                <select
                  className="w-full border rounded-lg p-3"
                  value={exp.univDegree}
                  onChange={(e) => handleChange(index, "univDegree", Number(e.target.value))}
                >
                  <option value="">{lang === "id" ? "Pilih Gelar" : "Select Degree"}</option>
                  {degrees.map((item: any) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>

              </div>

              <div>
                <label className="text-sm font-medium">{lang === "id" ? "Jurusan" : "Major"}</label>
                <input
                  className="w-full border rounded-lg p-3"
                  value={exp.univMajor}
                  onChange={(e) =>
                    handleChange(index, "univMajor", e.target.value)
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col">
                <label className="text-sm font-medium">{lang === "id" ? "Tanggal Mulai" : "Start Date"}</label>
                <DatePicker
                  selected={toDate(exp.startDate)}
                  onChange={(date: any) =>
                    handleChange(index, "startDate", toString(date))
                  }
                  dateFormat="yyyy-MM-dd"
                  placeholderText="YYYY-MM-DD"
                  className="w-full border rounded-lg p-3"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                />
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium">{lang === "id" ? "Tanggal Berkahir" : "End Date"}</label>
                <DatePicker
                  selected={toDate(exp.endDate)}
                  onChange={(date: any) =>
                    handleChange(index, "endDate", toString(date))
                  }
                  dateFormat="yyyy-MM-dd"
                  placeholderText="YYYY-MM-DD"
                  className="w-full border rounded-lg p-3"
                  disabled={exp.current}
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                />

              </div>
            </div>

            <label className="flex gap-2 items-center">
              <input
                type="checkbox"
                checked={exp.current}
                onChange={(e) => handleChange(index, "current", e.target.checked)}
              />
              {lang === "id" ? "Masih Belajar" : "Currently Studying"}
            </label>
          </div>
        </div>
      ))}

      <div className="flex justify-center">
        <button
          onClick={addEducation}
          className="flex items-center gap-2 text-primary font-semibold"
        >
          ➕ {lang === "id" ? "Tambah Pendidikan" : "Add Education"}
        </button>
      </div>

      <div className="flex justify-center gap-4 pt-4 pb-2">
        <button
          onClick={onClose}
          className="px-6 py-3 rounded-lg border text-gray-500 font-semibold"
        >
          {lang === "id" ? "Batal" : "Cancel"}
        </button>
        <button
          onClick={handleSave}
          className="px-6 py-3 rounded-lg bg-primary text-white font-semibold"
        >
          {lang === "id" ? "Simpan" : "Save"}
        </button>
      </div>
    </div>
  );
}
